import React from 'react'
import { graphql } from '@apollo/client/react/hoc'
import { Link } from 'react-router-dom'
import { Table, UncontrolledTooltip } from 'reactstrap'
import PrimaryListingContainer from "../Material/PrimaryListingContainer"
import RepAvatar from "../Material/RepAvatar"
import { AllAutomationsQuery } from "./Queries"
import moment from "moment"
import withApolloClient from '../withApolloClient'
import toTitleCase from '../Functions/toTitleCase'
import { useAdvancedFilters } from '../Filters/filterUrl'

const AUTOMATION_TABLE_HEADERS = [
    { title: '', key: 'index', className: 'select-all-checkbox' },
    { title: 'Actions', key: 'rowActions', className: 'actions-th' },
    { title: 'Automation', key: 'name' },
    { title: 'Status', key: 'status' },
    { title: 'Trigger', key: 'trigger' },
    { title: 'Workflow actions', key: 'actions' },
    { title: 'Last updated', key: 'dateModified' },
    { title: 'Owner', key: 'createdBy' },
]

export function AutomationList({ loadMoreEntries, allAutomations, loading }) {
    const automations = allAutomations?.edges || []
    const pageInfo = allAutomations?.pageInfo

    return (
        <div className="AutomationList">
            <PrimaryListingContainer
                listLoading={loading}
                totalFetchedRows={automations.length}
                pageInfo={pageInfo}
                loadMoreEntries={pageInfo?.hasNextPage ? loadMoreEntries : null}
                refetchListQuery={null}
            >
                <Table responsive className="automately-table">
                    <thead>
                        <tr>
                            {AUTOMATION_TABLE_HEADERS.map((header) => (
                                <th key={header.key} className={header.className}>{header.title}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {automations.length > 0 ? automations.map(({ node: automation }, automationIndex) => {
                            const actions = (automation.actionSet?.edges || [])
                                .map(({ node }) => node)
                                .filter(Boolean)
                            const remainingActions = actions.slice(2)
                            const remainingActionNames = remainingActions
                                .map((action) => toTitleCase(action.actionType))
                                .join(', ')
                            const remainingActionsTooltipId = `remainingActions${automationIndex}`
                            const automationDetailsTooltipId = `automationDetails${automationIndex}`
                            const automationName = toTitleCase(automation.name)
                            return (
                                <tr key={automation.id}>
                                    <td className="automately-row-index">
                                        <strong>{automationIndex + 1}</strong>
                                    </td>
                                    <td className="actions-td">
                                        <span className="action-item">
                                            <Link
                                                to={`/automations/${automation.id}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                aria-label={`View ${automation.name}`}
                                            >
                                                <i id={automationDetailsTooltipId} className="fa fa-external-link" aria-hidden="true"></i>
                                                <UncontrolledTooltip target={automationDetailsTooltipId} placement="bottom">View Automation</UncontrolledTooltip>
                                            </Link>
                                        </span>
                                    </td>
                                    <td className="automately-name-cell">
                                        <Link
                                            to={`/automations/${automation.id}`}
                                            className="automately-name-link"

                                            rel="noopener noreferrer"
                                        >
                                            <span>
                                                <strong title={automationName}>{automationName}</strong>
                                                <small>Created {moment(automation.dateAdded).format("MMM D, YYYY")}</small>
                                            </span>
                                        </Link>
                                    </td>
                                    <td>
                                        <span className={`automately-status ${automation.isActive ? 'active' : 'inactive'}`}>
                                            <span></span>{automation.isActive ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td className="automately-trigger-cell">
                                        {automation.trigger ? (
                                            <div className="automately-step">
                                                <span className="automately-step-icon trigger"><i className="fa fa-bolt" aria-hidden="true"></i></span>
                                                <span>
                                                    <strong>{toTitleCase(automation.trigger.triggerType)}</strong>
                                                    <small>{toTitleCase(automation.trigger.contentType?.model)}</small>
                                                </span>
                                            </div>
                                        ) : <span className="automately-empty-value">No trigger</span>}
                                    </td>
                                    <td className="automately-actions-cell">
                                        {actions.length > 0 ? (
                                            <div className="automately-action-list">
                                                {actions.slice(0, 2).map((action) => (
                                                    <span className="automately-action-chip" key={action.id} title={toTitleCase(action.actionType)}>
                                                        <i className="fa fa-cog" aria-hidden="true"></i>
                                                        {toTitleCase(action.actionType)}
                                                    </span>
                                                ))}
                                                {remainingActions.length > 0 && (
                                                    <>
                                                        <span
                                                            id={remainingActionsTooltipId}
                                                            className="automately-action-more"
                                                            tabIndex={0}
                                                            aria-label={`${remainingActions.length} more ${remainingActions.length === 1 ? 'action' : 'actions'}: ${remainingActionNames}`}
                                                        >
                                                            +{remainingActions.length}
                                                        </span>
                                                        <UncontrolledTooltip
                                                            placement="top"
                                                            target={remainingActionsTooltipId}
                                                        >
                                                            {remainingActionNames}
                                                        </UncontrolledTooltip>
                                                    </>
                                                )}
                                            </div>
                                        ) : <span className="automately-empty-value">No actions</span>}
                                    </td>
                                    <td className="automately-date-cell" title={moment(automation.dateModified || automation.dateAdded).format("MMMM D, YYYY [at] h:mm A")}>
                                        {moment(automation.dateModified || automation.dateAdded).fromNow()}
                                    </td>
                                    <td className="automately-owner-cell">
                                        <RepAvatar rep={automation.createdBy || undefined} />
                                    </td>
                                    <td className="automately-open-cell">
                                        <Link
                                            to={`/automations/${automation.id}`}
                                            aria-label={`Open ${automation.name}`}

                                            rel="noopener noreferrer"
                                        >
                                            <i className="fa fa-chevron-right" aria-hidden="true"></i>
                                        </Link>
                                    </td>
                                </tr>
                            )
                        }) : !loading && (
                            <tr>
                                <td colSpan={AUTOMATION_TABLE_HEADERS.length} className="automately-empty-state">
                                    <span className="automately-empty-icon"><i className="fa fa-code-fork" aria-hidden="true"></i></span>
                                    <strong>No automations found</strong>
                                    <span>Try another search or create a new workflow.</span>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </PrimaryListingContainer>
        </div>
    )
}

const AutomationListWithData = graphql(AllAutomationsQuery, {
    options: ({ searchTerm, filters }) => ({
        variables: {
            searchTerm,
            filters,
            first: 30,
        },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'cache-and-network'
    }),
    props: ({ data: { loading, allAutomations, fetchMore, variables } }) => ({
        loading,
        allAutomations,
        loadMoreEntries: () => {
            return fetchMore({
                query: AllAutomationsQuery,
                variables: {
                    ...variables,
                    after: allAutomations.pageInfo.endCursor
                },
                updateQuery: (previousResult, { fetchMoreResult }) => {
                    const newEdges = fetchMoreResult.allAutomations.edges
                    const pageInfo = fetchMoreResult.allAutomations.pageInfo

                    return {
                        allAutomations: {
                            edges: [...previousResult.allAutomations.edges, ...newEdges],
                            pageInfo,
                            searchTerm: previousResult.searchTerm,
                            __typename: previousResult.allAutomations.__typename
                        },
                    }
                },
            })
        },
    })
})(withApolloClient(AutomationList))

export default function AutomationListWithAdvancedFilters(props) {
    const [advancedFilters] = useAdvancedFilters()
    return <AutomationListWithData {...props} filters={advancedFilters} />
}
